import api from './api';
import {
  RestaurantGuide,
  RestaurantGuidePayload,
  RestaurantGuideUploadedImage,
  RestaurantGuideListResponse,
  ApiResponseEnvelope,
} from '../types/restaurant';

const toEnvelope = <T>(response: any): ApiResponseEnvelope<T> => {
  return response.data as ApiResponseEnvelope<T>;
};

export const restaurantGuidesService = {
  async getGuides(params?: {
    page?: number;
    limit?: number;
    exposure?: string;
    search?: string;
  }): Promise<RestaurantGuideListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.exposure && params.exposure !== '전체') {
      queryParams.append('exposure', params.exposure === '노출' ? 'show' : 'hide');
    }
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get(`/restaurant-guides?${queryParams.toString()}`);
    const { data } = toEnvelope<RestaurantGuideListResponse>(response);
    return data;
  },

  async getGuide(id: number | string): Promise<RestaurantGuide> {
    const response = await api.get(`/restaurant-guides/${id}`);
    const { data } = toEnvelope<RestaurantGuide>(response);
    return data;
  },

  async createGuide(payload: RestaurantGuidePayload): Promise<RestaurantGuide> {
    const response = await api.post('/restaurant-guides', payload);
    const { data } = toEnvelope<RestaurantGuide>(response);
    return data;
  },

  async updateGuide(id: number | string, payload: Partial<RestaurantGuidePayload>): Promise<RestaurantGuide> {
    const response = await api.put(`/restaurant-guides/${id}`, payload);
    const { data } = toEnvelope<RestaurantGuide>(response);
    return data;
  },

  async deleteGuide(id: number | string): Promise<void> {
    await api.delete(`/restaurant-guides/${id}`);
  },

  async deleteGuides(ids: (number | string)[]): Promise<void> {
    await api.post('/restaurant-guides/bulk-delete', { ids });
  },

  async updateExposure(ids: (number | string)[], exposure: 'show' | 'hide'): Promise<void> {
    await api.patch('/restaurant-guides/bulk-exposure', { ids, exposure });
  },

  async getLayouts(): Promise<string[]> {
    const response = await api.get('/restaurant-guides/layouts');
    const { data } = toEnvelope<string[]>(response);
    return data;
  },

  async uploadImages(files: File[]): Promise<RestaurantGuideUploadedImage[]> {
    if (!files.length) {
      return [];
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const response = await api.post('/upload/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const { data } = toEnvelope<RestaurantGuideUploadedImage[]>(response);
    return data;
  },

  async downloadImagesZip(id: number | string): Promise<Blob> {
    const response = await api.get(`/restaurant-guides/${id}/download-images`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default restaurantGuidesService;
